/**
 * Host-owned store deciding which `Dismissible` keys the member has dismissed.
 *
 * A document can only name what was dismissed; where that is remembered (the
 * device, the member's account) is the host's choice, so the host registers an
 * implementation at startup. Until it does, dismissals live in memory for the
 * life of the process — a press still hides the content, it is just forgotten
 * on the next launch.
 */

import { useSyncExternalStore } from 'react';

export interface DismissalStore {
  isDismissed(key: string): boolean;
  dismiss(key: string): void;
  /** Called with no arguments whenever any key's answer may have changed. */
  subscribe(listener: () => void): () => void;
}

function createMemoryDismissalStore(): DismissalStore {
  const dismissed = new Set<string>();
  const listeners = new Set<() => void>();
  return {
    isDismissed: (key) => dismissed.has(key),
    dismiss: (key) => {
      dismissed.add(key);
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

let activeStore: DismissalStore = createMemoryDismissalStore();

export function registerDismissalStore(store: DismissalStore): void {
  activeStore = store;
}

export function getDismissalStore(): DismissalStore {
  return activeStore;
}

/** Test seam — restores the in-memory default. */
export function resetDismissalStore(): void {
  activeStore = createMemoryDismissalStore();
}

/** Whether `key` is dismissed, re-rendering the caller when the answer changes. */
export function useDismissed(key: string): boolean {
  const store = activeStore;
  return useSyncExternalStore(
    store.subscribe,
    () => store.isDismissed(key),
    () => store.isDismissed(key),
  );
}
